import {Injectable} from '@angular/core';
import {LoggerFactory} from "../javascript.lib.mojo-base/log/LoggerFactory";
import {AppProperty, IAppProperty} from "../javascript.lib.mojo-base/model/AppProperty";
import {FirebaseProperty} from "../javascript.lib.mojo-base/firebase/realtime-database/properties/FirebaseProperty";
import {SessionContextProvider} from "../service.session-context/session-context-provider";
import {FirebaseConnectionService} from "../common/service.firebase-connection/FirebaseConnectionService";
import {PropertyContext} from "./property-context";
import {BehaviorSubject} from "rxjs";
import {EProductType, ProductTypeOptions} from "../javascript.lib.mojo-base/model/ProductType";
import {ProductBuilder} from "../service.product-builder/product-builder.service";
import {Subscriptions2} from "../javascript.lib.mojo-base/util/Subscriptions2";
import {SessionContextState} from "../common/service.session-context/BaseSessionContext";

@Injectable(
  {providedIn: 'root'}
)
export class PropertyService {

  private static _log = LoggerFactory.build( 'PropertyService' );
  private properties: AppProperty[];
  private property: AppProperty;
  private _subscriptions: Subscriptions2 = new Subscriptions2();

  public propertySubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public propertyContext: PropertyContext|null = null;

  get productType() : EProductType {
    return this.propertyContext.property.value.productType;
  }

  get productDetails() : string {
    const product = this.propertyContext?.product;
    if (!product) {
      return;
    }
    const productLabel = ProductTypeOptions.getLabel(product.productType);
    const productVersion = product.version;
    return `${productLabel} ${productVersion}`;
  }

  public async getPropertiesForClient() : Promise<AppProperty[]> {
    if (!this.sessionContext.isAdministrator) {
      return;
    }
    return await FirebaseProperty.readAll(this.firebase, this.sessionContext.clientKey);
  }

  public async getPropertiesForCurrentUser() : Promise<AppProperty[]> {

    try {
      const properties: AppProperty[] = [];
      const user = this.sessionContext.user;

      if (!user || !user.value || !this.sessionContext.clientKey) {

        PropertyService._log.error("Error getting user", "user", user);

      } else if (this.sessionContext.isAdministrator) {

        const temporaryPropertyKey = user.value.temporaryPropertyKey;
        const property = await FirebaseProperty.read(this.firebase, this.sessionContext.clientKey, temporaryPropertyKey);
        if (property) {
          properties.push(property);
        }

      } else {

        let clientProperties = await FirebaseProperty.readAll(this.firebase, this.sessionContext.clientKey);
        if (user.value.propertyKeys) {
          for (const propertyKey of user.propertyKeys) {
            let property = clientProperties.find(p => p.propertyKey === propertyKey)
            if (property) {
              properties.push(property);
            }
          }
        }
      }

      return properties;

    } catch (e) {
      PropertyService._log.error("Error in getAllPropertiesForUser", e);
    }
  }

  public async getPropertyForCurrentUser(propertyKey: string) : Promise<AppProperty> {
    if (!propertyKey) {
      return;
    }

    if (this.property && this.property.propertyKey == propertyKey) {
      return this.property;
    }

    if (this.properties && this.properties[propertyKey]) {
      this.property = this.properties[propertyKey];
    } else {
      this.property = await FirebaseProperty.read(this.firebase, this.sessionContext.clientKey, propertyKey);
    }

    return this.property;
  }

  public async getOrBuildPropertyContext(propertyKey: string) : Promise<PropertyContext> {
    if (!propertyKey) {
      return;
    }

    let property: AppProperty
    if (propertyKey && this.propertyContext?.property?.propertyKey !== propertyKey) {
      property = await this.getPropertyForCurrentUser(propertyKey);
      if (property) {
        return await this.buildPropertyContextWithProperty(property);
      }
    }

    if (!property) {
      PropertyService._log.warn("buildPropertyContext !property", "propertyKey", propertyKey ?? "null");
    }
  }

  public async buildPropertyContextWithProperty(property: AppProperty) : Promise<PropertyContext> {
    this.propertyContext = await PropertyContext.build(this.sessionContext, property, this.productBuilder);
    this.propertySubject.next(property.propertyKey);

    return this.propertyContext;
  }

  private reset() : void {
    this.property = null;
    this.properties = null;
    this.propertyContext = null;
  }

  public async write(property: AppProperty) : Promise<void> {
    return await FirebaseProperty.write(this.firebase, this.sessionContext.clientKey, property);
  }

  constructor(public firebase: FirebaseConnectionService,
              public sessionContext: SessionContextProvider,
              public productBuilder: ProductBuilder,
  ) {
    this._subscriptions.subscribe(sessionContext.stateSubject, (state) => {
      if (state === SessionContextState.LoggedOut) {
        PropertyService._log.info("Resetting property-service state");
        this.reset();
      }
    });
    this._subscriptions.subscribe(sessionContext.clientKeySubject, (clientKey) => {
      this.reset();
    })
  }
}
